// Period choice (mockup ui.periodSeg / ui.dateRangeFields / App.rangeProblem, element 030):
// [오늘|7일|30일|90일|직접] and, for [직접], two browser date fields — at most 366 days, no future dates, start ≤ end.
// The date fields' max only limits the calendar, so typed dates are checked again on [적용].
import { forwardRef, useId, useImperativeHandle, useRef, useState } from 'react';
import { useFmt, useT } from '../i18n';
import type { Fmt, TFn } from '../lib/format';
import { isMobile } from '../lib/media';
import { DAY, dayStart, isCustomPeriod, parseYmd, periodRange, ymd, type CustomPeriod, type Period, type PeriodKey, type PeriodRange } from '../lib/time';
import { cls } from '../lib/util';
import { useViewStore } from '../state/store';
import type { Method } from '../state/url';
import { Field, Seg } from './Controls';
import { Modal } from './Modal';
import { Popover, focusBack } from './Popover';

/** null when the range is fine, otherwise the message and which field (0 start, 1 end) to fix. */
export function rangeProblem(from: string, to: string, t: TFn, fmt: Fmt, now = Date.now()): { msg: string; at: 0 | 1 } | null {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(from || '') || !re.test(to || '')) return { msg: t('period.err.empty'), at: re.test(from || '') ? 1 : 0 };
  const today = ymd(now);
  if (from > today || to > today) return { msg: t('period.err.future', { today: fmt.calendarDate(today, 'md') }), at: from > today ? 0 : 1 };
  if (from > to) return { msg: t('period.invalid'), at: 0 };
  const n = Math.round((parseYmd(to) - parseYmd(from)) / DAY) + 1;
  return n > 366 ? { msg: t('period.err.long', { n }), at: 0 } : null;
}

/** First [직접] choice: the last 14 days. */
export const defaultRange = (now = Date.now()): CustomPeriod => ({ from: ymd(dayStart(now) - 13 * DAY), to: ymd(now) });

export interface PeriodLabel {
  /** "최근 7일" · "고른 기간" */
  short: string;
  /** the days the period covers so far: "10월 14일~10월 20일" */
  range: string;
  pr: PeriodRange;
  /** a picked range with its day count — "10월 1일~10월 20일 · 20일" (plan 9.1.4) — otherwise null */
  sum: string | null;
}
/** Words for a period (mockup ui.periodLabel); without an argument, the common period of the tool bar. */
export function usePeriodLabel(period?: Period): PeriodLabel {
  const t = useT();
  const fmt = useFmt();
  const common = useViewStore((s) => s.period);
  const p = period ?? common;
  const now = Date.now();
  const pr = periodRange(p, now);
  return {
    short: t('period.long.' + (isCustomPeriod(p) ? 'custom' : p)),
    range: fmt.range(pr.from, Math.min(pr.to, now) - 1),
    pr,
    sum: isCustomPeriod(p) ? fmt.rangeSum(p.from, p.to) : null,
  };
}

export interface DateRangeHandle {
  /** true when valid; otherwise shows the reason and focuses the field to fix. */
  check(): boolean;
}
export interface DateRangeFieldsProps {
  value: CustomPeriod;
  onChange: (v: CustomPeriod) => void;
  className?: string;
}
export const DateRangeFields = forwardRef<DateRangeHandle, DateRangeFieldsProps>(function DateRangeFields({ value, onChange, className }, ref) {
  const t = useT();
  const fmt = useFmt();
  const errId = useId();
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<{ msg: string; at: 0 | 1 } | null>(null);
  const bad = rangeProblem(value.from, value.to, t, fmt);
  useImperativeHandle(ref, () => ({
    check() {
      const p = rangeProblem(value.from, value.to, t, fmt);
      setErr(p);
      if (p) (p.at ? toRef.current : fromRef.current)?.focus();
      return !p;
    },
  }));
  const shownErr = err && bad ? err : null;
  const max = ymd(Date.now());
  return (
    <div className={cls('date-range', className)}>
      <div className="date-range__grid">
        <Field
          label={t('period.from')}
          type="date"
          value={value.from}
          max={max}
          inputRef={fromRef}
          aria-describedby={errId}
          aria-invalid={shownErr?.at === 0 ? true : undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
        />
        <Field
          label={t('period.to')}
          type="date"
          value={value.to}
          max={max}
          inputRef={toRef}
          aria-describedby={errId}
          aria-invalid={shownErr?.at === 1 ? true : undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
        />
      </div>
      <p className="date-range__sum" aria-live="polite">
        {bad ? '' : fmt.rangeSum(value.from, value.to)}
      </p>
      <p className="field__error" id={errId} aria-live="polite">
        {shownErr?.msg ?? ''}
      </p>
      <p className="field__hint">{t('period.limitHint')}</p>
    </div>
  );
});

type PeriodChoice = PeriodKey | 'custom';

export interface PeriodSegProps {
  /** Default: the common period (store). */
  value?: Period;
  /** Default: set the common period. */
  onChange?: (p: Period) => void;
  size?: 'sm';
  /** Inside an option panel: [직접] only reports a range; the panel shows DateRangeFields in place. */
  inline?: boolean;
}
/** [오늘|7일|30일|90일|직접] — 직접 opens a date-range popover (PC) or sheet (phone). */
export function PeriodSeg({ value, onChange, size, inline }: PeriodSegProps) {
  const t = useT();
  const fmt = useFmt();
  const storePeriod = useViewStore((s) => s.period);
  const setPeriod = useViewStore((s) => s.setPeriod);
  const cur = value ?? storePeriod;
  const change = onChange ?? setPeriod;
  const custom = isCustomPeriod(cur);
  const [picking, setPicking] = useState<null | 'pop' | 'sheet'>(null);
  const [draft, setDraft] = useState<CustomPeriod>(() => (isCustomPeriod(cur) ? cur : defaultRange()));
  const fieldsRef = useRef<DateRangeHandle>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const segRef = useRef<HTMLDivElement>(null);

  const open = () => {
    setDraft(isCustomPeriod(cur) ? { ...cur } : defaultRange());
    anchorRef.current = segRef.current?.querySelector<HTMLElement>('[data-value="custom"]') ?? null;
    setPicking(isMobile() ? 'sheet' : 'pop');
  };
  const apply = () => {
    if (!fieldsRef.current?.check()) return false;
    setPicking(null);
    change({ from: draft.from, to: draft.to });
    return true;
  };
  const close = (returnFocus: boolean) => {
    setPicking(null);
    if (returnFocus) focusBack(anchorRef.current);
  };
  const items = (['today', '7d', '30d', '90d', 'custom'] as PeriodChoice[]).map((k) => ({
    value: k,
    label: t('period.' + k),
    extra: k === 'custom' && custom && !inline ? <small className="seg__sum">{fmt.rangeSum((cur as CustomPeriod).from, (cur as CustomPeriod).to)}</small> : undefined,
  }));
  const shown: PeriodChoice = picking ? 'custom' : custom ? 'custom' : (cur as PeriodKey);
  const fields = <DateRangeFields ref={fieldsRef} value={draft} onChange={setDraft} />;
  return (
    <>
      <div ref={segRef} style={{ display: 'contents' }}>
        <Seg<PeriodChoice>
          label={t('period.label')}
          size={size}
          value={shown}
          items={items}
          always
          onChange={(v) => {
            if (v !== 'custom') change(v);
            else if (inline) change(custom ? cur : defaultRange());
            else open();
          }}
        />
      </div>
      {picking === 'pop' ? (
        <Popover anchorRef={anchorRef} open onClose={close} label={t('period.customTitle')} width={340}>
          <div className="date-range">
            <p className="popover__title">{t('period.customTitle')}</p>
            {fields}
            <div className="popover__foot">
              <button className="btn btn--sm" type="button" onClick={() => close(true)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn--primary btn--sm" type="button" onClick={() => apply() && focusBack(anchorRef.current)}>
                {t('common.apply')}
              </button>
            </div>
          </div>
        </Popover>
      ) : null}
      <Modal
        open={picking === 'sheet'}
        onClose={() => setPicking(null)}
        kicker={t('period.label')}
        title={t('period.customTitle')}
        className="sheet--panel"
        actions={[
          { label: t('common.cancel'), value: null },
          { label: t('common.apply'), primary: true, value: true, onClick: () => apply() },
        ]}
      >
        {picking === 'sheet' ? fields : null}
      </Modal>
    </>
  );
}

/** [전체 | CLI 도구 | API 키 호출] — the common usage method (address query `method`). */
export function MethodSeg({ value, onChange, size = 'sm' }: { value?: Method; onChange?: (m: Method) => void; size?: 'sm' }) {
  const t = useT();
  const storeMethod = useViewStore((s) => s.method);
  const setMethod = useViewStore((s) => s.setMethod);
  return (
    <Seg<Method>
      label={t('method.label')}
      size={size}
      value={value ?? storeMethod}
      onChange={onChange ?? setMethod}
      items={(['all', 'cli', 'api'] as Method[]).map((m) => ({ value: m, label: t('method.' + m), icon: m === 'cli' ? 'terminal' : m === 'api' ? 'key' : undefined }))}
    />
  );
}
